import React, { useState } from "react";
import { updateNotificationPrefs } from "@/services/auth.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Section } from "./SettingsShared";

const EVENT_GROUPS = [
  { group: "Contracts", events: [
    ["contract_invited", "Contract invitations"],
    ["contract_accepted", "Contract accepted"],
    ["contract_declined", "Contract declined"],
  ] },
  { group: "Milestones", events: [
    ["milestone_funded", "Milestone funded"],
    ["milestone_submitted", "Work submitted"],
    ["changes_requested", "Changes requested"],
    ["milestone_approved", "Work approved"],
    ["fund_reminder", "Funding reminders"],
  ] },
  { group: "Invoices", events: [
    ["invoice_received", "Invoice received"],
    ["invoice_funded", "Invoice funded"],
    ["invoice_cancelled", "Invoice cancelled"],
    ["invoice_disputed", "Invoice disputed"],
  ] },
  { group: "Payments", events: [
    ["payment_released", "Payment released"],
  ] },
  { group: "Disputes", events: [
    ["dispute_opened", "Dispute opened"],
    ["dispute_updated", "Dispute updated"],
    ["dispute_resolved", "Dispute resolved"],
  ] },
];

const ALL_EVENTS = EVENT_GROUPS.flatMap((g) => g.events.map(([k]) => k));

const NotificationsTab = ({ user, refreshUser }) => {
  const [prefs, setPrefs] = useState({
    channel: user.notification_prefs?.channel || "in_app",
    events: { ...Object.fromEntries(ALL_EVENTS.map((k) => [k, true])), ...(user.notification_prefs?.events || {}) },
  });
  const [saving, setSaving] = useState(false);

  const setEvent = (k, v) => setPrefs((p) => ({ ...p, events: { ...p.events, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await updateNotificationPrefs(prefs); toast.success("Preferences saved"); await refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Section title="Notifications" description="Choose how and when PAYD reaches out. Turning an item off silences both in-app and email." data-testid="notifications-section">
      <div>
        <Label>Channel</Label>
        <select value={prefs.channel} onChange={(e) => setPrefs({ ...prefs, channel: e.target.value })} className="mt-1.5 h-11 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="notif-channel-select">
          <option value="in_app">In-app only</option>
          <option value="in_app_email">In-app + email</option>
        </select>
      </div>
      <div className="space-y-5">
        {EVENT_GROUPS.map(({ group, events }) => (
          <div key={group}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">{group}</div>
            <div className="space-y-2">
              {events.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between border border-border rounded-lg px-4 py-2.5">
                  <span className="text-sm font-medium">{l}</span>
                  <Switch checked={prefs.events[k] !== false} onCheckedChange={(v) => setEvent(k, v)} data-testid={`notif-${k}-switch`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="notif-save-btn">{saving ? "Saving…" : "Save preferences"}</Button></div>
    </Section>
  );
};

export default NotificationsTab;
